import { useState } from "react";
import { Lock, User, Eye, EyeOff, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showRecover, setShowRecover] = useState(false);
  const [recoverHint, setRecoverHint] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Enter username and password");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { username, password });
      localStorage.setItem("meatprint_token", res.data.token);
      localStorage.setItem("meatprint_user", res.data.username);
      onLogin(res.data.token);
      toast.success("Welcome back");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleRecover = async () => {
    if (!username) {
      toast.error("Enter your username first");
      return;
    }
    try {
      const res = await axios.post(`${API}/auth/recover`, { username });
      setRecoverHint(res.data.email_hint);
      toast.success("Recovery information shown below");
    } catch {
      toast.error("User not found");
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 noise-bg">
      <div className="w-full max-w-md animate-fade-in-up">
        {/* Logo Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-sm flex items-center justify-center mx-auto mb-4">
            <span className="text-primary-foreground font-bold text-3xl font-mono">M</span>
          </div>
          <h1
            className="text-4xl font-bold tracking-wider uppercase"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            MEATPRINT
          </h1>
          <p className="text-xs text-muted-foreground tracking-widest uppercase mt-1">
            LABEL PRINTING SYSTEM
          </p>
        </div>

        {/* Login Card */}
        <Card className="bg-card border border-border/50 rounded-sm corner-brackets">
          <CardContent className="p-6 relative z-10">
            <h2
              className="text-xl font-bold tracking-wider uppercase mb-6 text-center"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              {showRecover ? "RECOVER PASSWORD" : "SIGN IN"}
            </h2>

            {!showRecover ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground">
                    USERNAME
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      data-testid="login-username"
                      placeholder="Enter username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      autoComplete="username"
                      className="h-14 pl-12 bg-input border-2 border-border focus:border-primary text-lg font-mono rounded-sm"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground">
                    PASSWORD
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      data-testid="login-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      className="h-14 pl-12 pr-12 bg-input border-2 border-border focus:border-primary text-lg font-mono rounded-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      data-testid="toggle-password"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  data-testid="login-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full h-14 text-lg font-bold uppercase tracking-wider rounded-sm glow-orange bg-primary text-primary-foreground hover:brightness-110 active:scale-95 transition-all duration-75 mt-2"
                >
                  {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Lock className="w-5 h-5 mr-2" />}
                  SIGN IN
                </Button>

                <button
                  type="button"
                  onClick={() => setShowRecover(true)}
                  className="w-full text-center text-xs text-muted-foreground hover:text-primary tracking-wider uppercase mt-3 transition-colors"
                  data-testid="forgot-password-link"
                >
                  FORGOT PASSWORD?
                </button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground">
                    USERNAME
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      data-testid="recover-username"
                      placeholder="Enter your username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="h-14 pl-12 bg-input border-2 border-border focus:border-primary text-lg font-mono rounded-sm"
                    />
                  </div>
                </div>

                <Button
                  data-testid="recover-submit-btn"
                  onClick={handleRecover}
                  className="w-full h-14 text-base font-bold uppercase tracking-wider rounded-sm bg-primary text-primary-foreground"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  SHOW RECOVERY EMAIL
                </Button>

                {recoverHint && (
                  <div className="bg-secondary/50 border border-border rounded-sm p-4 text-center" data-testid="recover-hint">
                    <p className="text-xs text-muted-foreground tracking-wider uppercase mb-1">RECOVERY EMAIL</p>
                    <p className="text-lg font-mono text-primary font-bold">{recoverHint}</p>
                    <p className="text-[10px] text-muted-foreground mt-2">
                      Contact this email to recover your password
                    </p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => { setShowRecover(false); setRecoverHint(""); }}
                  className="w-full text-center text-xs text-muted-foreground hover:text-primary tracking-wider uppercase mt-2 transition-colors"
                  data-testid="back-to-login-link"
                >
                  BACK TO SIGN IN
                </button>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-[10px] text-muted-foreground/40 tracking-wider uppercase mt-6">
          AUTHORIZED PERSONNEL ONLY
        </p>
      </div>
    </div>
  );
}
